var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React from 'react';
import { getJson, postJson, withDone } from '@rad/utility/http';
// -------------------------------------------------------------------
// CRM status (real backend, GET /merchants/{id}/crm/status)
// -------------------------------------------------------------------
const DEFAULT_STATUS = { 'state': 'not_started' };
const POLL_INTERVAL_MS = 15000; // background refresh while the tab is open
export const fetchCRMStatus = (merchantId) => __awaiter(void 0, void 0, void 0, function* () {
    const response = yield withDone(getJson(`/merchants/${merchantId}/crm/status`))();
    return {
        'state': response.state,
        'phone_number': response.phone_number,
        'rejection_reason': response.rejection_reason,
        'location_id': response.location_id,
    };
});
const CRM_STATUS_REFRESHED_EVENT = 'crm-status-refreshed';
// Notify any mounted useCRMStatus subscribers to re-fetch. Called after
// merchant-initiated state transitions (payment confirm, registration
// submit, etc.) so the UI updates without waiting for the poll tick.
export const refreshCRMStatus = () => {
    window.dispatchEvent(new CustomEvent(CRM_STATUS_REFRESHED_EVENT));
};
// Callers resolve the merchant during render.
export const useCRMStatus = (merchantId) => {
    const [status, setStatus] = React.useState({ merchantId, 'value': DEFAULT_STATUS });
    React.useEffect(() => {
        let cancelled = false;
        let timer = null;
        const tick = () => __awaiter(void 0, void 0, void 0, function* () {
            // Cancel any pending scheduled tick before running — prevents
            // duplicate pollers from accumulating when refreshCRMStatus() is
            // invoked between periodic ticks.
            if (timer) {
                clearTimeout(timer);
                timer = null;
            }
            try {
                const next = yield fetchCRMStatus(merchantId);
                if (!cancelled) {
                    setStatus({ merchantId, 'value': next });
                }
            }
            catch ( /* ignore transient errors */_a) { /* ignore transient errors */ }
            if (!cancelled) {
                timer = setTimeout(tick, POLL_INTERVAL_MS);
            }
        });
        tick();
        const onRefresh = () => tick();
        window.addEventListener(CRM_STATUS_REFRESHED_EVENT, onRefresh);
        return () => {
            cancelled = true;
            if (timer) {
                clearTimeout(timer);
            }
            window.removeEventListener(CRM_STATUS_REFRESHED_EVENT, onRefresh);
        };
    }, [merchantId]);
    return status.merchantId === merchantId ? status.value : DEFAULT_STATUS;
};
// The workspace can flip to `active` before Twilio finishes provisioning the
// number (A2P approval + phone assignment webhooks race). Once state is active
// the merchant can view existing conversations and unread messages, so gate
// only on state — send flows still guard on phone availability at send time.
export const isCRMMessagingAvailable = (status) => status.state === 'active';
export const subscribeCRM = (merchantId) => withDone(postJson(`/merchants/${merchantId}/crm/subscribe`, {}))();
export const fetchCRMBilling = (merchantId) => withDone(getJson(`/merchants/${merchantId}/crm/billing`))();
export const cancelCRMSubscription = (merchantId) => withDone(postJson(`/merchants/${merchantId}/crm/cancel`, {}))();
export const reactivateCRMSubscription = (merchantId) => withDone(postJson(`/merchants/${merchantId}/crm/reactivate`, {}))();
export const updateCRMCard = (merchantId) => withDone(postJson(`/merchants/${merchantId}/crm/billings/update`, {}))();
